import { Component, createContext, useContext } from "react";
import ReactDOM from "react-dom";
import { uniqueId, omit } from "lodash";

import { Consumer as AppContainerConsumer } from "../Main/AppContainerContext";
import { getDisplayName } from "../Utils/getDisplayName";

const PortalContext = createContext(null);

class PortalServiceBase extends Component {
  static displayName = "PortalService";

  constructor(props) {
    super(props);
    this.showPortal = this.showPortal.bind(this);
    this.hidePortal = this.hidePortal.bind(this);

    this.state = { portals: {} };
  }

  get providedContext() {
    return {
      showPortal: this.showPortal,
      hidePortal: this.hidePortal,
      isPortalShown: this.state.isPortalShown
    };
  }

  get portals() {
    return Object.values(this.state.portals).map(portal =>
      ReactDOM.createPortal(portal, this.props.appContainer)
    );
  }

  showPortal(portal) {
    const portalId = uniqueId("portal");
    const portals = { ...this.state.portals, [portalId]: portal };
    this.setState({ portals });

    return portalId;
  }

  hidePortal(portalId) {
    const portals = omit(this.state.portals, [portalId]);
    this.setState({ portals });
  }

  render() {
    return (
      <PortalContext.Provider value={this.providedContext}>
        {this.props.children}
        {this.portals}
      </PortalContext.Provider>
    );
  }
}

export function PortalService(props) {
  return (
    <AppContainerConsumer>
      {appContainer => <PortalServiceBase {...props} appContainer={appContainer} />}
    </AppContainerConsumer>
  );
}

export function withPortal(WrappedComponent) {
  const WithPortal = props => {
    return (
      <PortalContext.Consumer>
        {portalProps => <WrappedComponent {...props} {...portalProps} />}
      </PortalContext.Consumer>
    );
  };

  WithPortal.displayName = `WithPortal(${getDisplayName(WrappedComponent)})`;

  return WithPortal;
}

export const usePortal = () => {
  const context = useContext(PortalContext);

  if (!context) {
    throw new Error("usePortal must be used within a PortalService");
  }

  return context;
};
